import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ContactUs.css';
import { LoginNavbar } from '../../components/layout/Navbar';
import { FaLinkedin, FaTwitter, FaFacebook } from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from 'react-i18next';

// FIX: configure default marker icons
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const Footer = ({ className, children }) => (
  <footer className={className || 'footer'}>
    {children}
  </footer>
);

const ContactUs = () => {
  const { t } = useTranslation();
  const position = [43.7772294, -79.2512385]; // 200 Consilium Pl coords

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });

      setTimeout(() => setSubmitStatus(null), 5000);
    }, 2000);
  };

  return (
    <>
      <LoginNavbar />
      <div className="contact-container">
        <div className="contact-back-button">
          <Link to="/login" className="back-button">
            ← {t('contact.backToLogin')}
          </Link>
        </div>

        <div className="contact-hero">
          <div className="contact-hero-content">
            <h1 className="contact-title">{t('contact.title')}</h1>
            <p className="contact-subtitle">{t('contact.subtitle')}</p>
          </div>
        </div>

        <div className="contact-content">
          {/* Left Column */}
          <div className="contact-main-column">
            <div className="contact-main">
              <h2 className="contact-section-title">{t('contact.form.title')}</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form-row">
                  <div className="contact-form-group">
                    <label htmlFor="name" className="contact-label">{t('contact.form.fullName')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="contact-input"
                      required
                      placeholder={t('contact.form.fullNamePlaceholder')}
                    />
                  </div>
                  <div className="contact-form-group">
                    <label htmlFor="email" className="contact-label">{t('contact.form.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="contact-input"
                      required
                      placeholder={t('contact.form.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div className="contact-form-row">
                  <div className="contact-form-group">
                    <label htmlFor="company" className="contact-label">{t('contact.form.company')}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="contact-input"
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>
                  <div className="contact-form-group">
                    <label htmlFor="subject" className="contact-label">{t('contact.form.subject')}</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="contact-input"
                      required
                      placeholder={t('contact.form.subjectPlaceholder')}
                    />
                  </div>
                </div>

                <div className="contact-form-group">
                  <label htmlFor="message" className="contact-label">{t('contact.form.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="contact-textarea"
                    rows="6"
                    required
                    placeholder={t('contact.form.messagePlaceholder')}
                  ></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div className="contact-success-message">
                    {t('contact.form.success')}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="contact-submit-button"
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
              </form>
            </div>

            {/* Map Section */}
            <div className="contact-map-section">
              <MapContainer
                center={position}
                zoom={15}
                scrollWheelZoom={false}
                style={{ height: "300px", width: "100%", borderRadius: "12px" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                />
                <Marker position={position}>
                  <Popup>
                    {t('contact.map.popupLine1')} <br />
                    {t('contact.map.popupLine2')} <br />
                    {t('contact.map.popupLine3')} <br />
                    {t('contact.map.popupLine4')}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          {/* Sidebar */}
          <div className="contact-sidebar">
            <div className="contact-info-section">
              <h3 className="contact-info-title">{t('contact.info.title')}</h3>

              <div className="contact-info-item">
                <div className="contact-info-icon">📍</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.address.label')}</h4>
                  <p>
                    {t('contact.info.address.valueLine1')}<br />
                    {t('contact.info.address.valueLine2')}<br />
                    {t('contact.info.address.valueLine3')} <br />
                    {t('contact.info.address.valueLine4')}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">📞</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.phone.label')}</h4>
                  <p>
                    {t('contact.info.phone.valueLine1')}<br />
                    {t('contact.info.phone.valueLine2')}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">✉️</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.email.label')}</h4>
                  <p>{t('contact.info.email.value')}</p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">🌐</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.website.label')}</h4>
                  <p>{t('contact.info.website.value')}</p>
                </div>
              </div>
            </div>

            <div className="contact-support-section">
              <h3 className="contact-support-title">{t('contact.support.title')}</h3>
              <p>{t('contact.support.description')}</p>
              <button className="contact-support-link">
                {t('contact.support.button')}
                <span className="contact-phone">📞 {t('contact.support.phone')}</span>
              </button>
            </div>

            <div className="contact-social-section">
              <h3 className="contact-social-title">{t('contact.social.title')}</h3>
              <div className="contact-social-links">
                <a href="#" className="contact-social-link"><FaLinkedin /></a>
                <a href="#" className="contact-social-link"><FaTwitter /></a>
                <a href="#" className="contact-social-link"><FaFacebook /></a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer className="contact-footer">{t('layout.footer')}</Footer>
    </>
  );
};

export default ContactUs;